import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { adminAPI } from '../lib/api';
import { formatPrice, formatDate, getStatusBadgeColor, getErrorMessage } from '../lib/utils';
import { BillingTab } from '../components/admin/BillingTab';
import { StripeTab } from '../components/admin/StripeTab';
import { toast } from 'sonner';
import {
  Users,
  Package,
  DollarSign,
  TrendingUp,
  Search,
  Eye,
  Pencil,
  Star,
  StarOff,
  Ban,
  CheckCircle,
  BarChart3,
  MessageSquare,
  FolderTree,
  Plus,
  Trash2,
  X,
  Loader2,
  GripVertical,
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#FF4F00', '#0F172A', '#64748B', '#94A3B8', '#CBD5E1'];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userFilter, setUserFilter] = useState('');
  const [listingFilter, setListingFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [listingSearch, setListingSearch] = useState('');

  // Category management state
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', image_url: '', subcategories: '', display_order: 0, active: true });
  const [categorySaving, setCategorySaving] = useState(false);

  // Make (marca) management state
  const [makes, setMakes] = useState([]);
  const [showMakeForm, setShowMakeForm] = useState(false);
  const [editingMake, setEditingMake] = useState(null);
  const [makeForm, setMakeForm] = useState({ name: '', categories: [], status: 'active', display_order: 0 });
  const [makeSaving, setMakeSaving] = useState(false);
  const [makeStatusFilter, setMakeStatusFilter] = useState('all');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [analyticsRes, usersRes, listingsRes, categoriesRes, makesRes] = await Promise.all([
          adminAPI.getAnalytics(),
          adminAPI.getUsers({ limit: 100 }),
          adminAPI.getListings({ limit: 100 }),
          adminAPI.getCategories(),
          adminAPI.getMakes(),
        ]);
        setAnalytics(analyticsRes.data);
        setUsers(usersRes.data);
        setListings(listingsRes.data);
        setCategories(categoriesRes.data);
        setMakes(makesRes.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, navigate]);

  const handleSuspendUser = async (userId, suspend) => {
    try {
      if (suspend) {
        await adminAPI.suspendUser(userId);
        toast.success('Usuario suspendido');
      } else {
        await adminAPI.activateUser(userId);
        toast.success('Usuario reactivado');
      }
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, status: suspend ? 'suspended' : 'active' } : u
      ));
    } catch (error) {
      toast.error('No se pudo actualizar el usuario');
    }
  };

  const handleFeatureListing = async (listingId, feature) => {
    try {
      await adminAPI.featureListing(listingId, feature);
      toast.success(feature ? 'Listing featured' : 'Listing unfeatured');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, featured: feature } : l
      ));
    } catch (error) {
      toast.error('No se pudo actualizar el anuncio');
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      await adminAPI.approveListing(listingId);
      toast.success('Listing approved');
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: 'active' } : l
      ));
    } catch (error) {
      toast.error('Failed to approve listing');
    }
  };

  // Category handlers
  const openCategoryForm = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({
        name: cat.name,
        description: cat.description || '',
        image_url: cat.image_url || '',
        subcategories: (cat.subcategories || []).join(', '),
        display_order: cat.display_order,
        active: cat.active,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', image_url: '', subcategories: '', display_order: categories.length, active: true });
    }
    setShowCategoryForm(true);
  };

  const closeCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setCategorySaving(true);
    try {
      const payload = {
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim() || null,
        image_url: categoryForm.image_url.trim() || null,
        subcategories: categoryForm.subcategories ? categoryForm.subcategories.split(',').map(s => s.trim()).filter(Boolean) : [],
        display_order: Number(categoryForm.display_order) || 0,
        active: categoryForm.active,
      };
      if (editingCategory) {
        const res = await adminAPI.updateCategory(editingCategory.id, payload);
        setCategories(prev => prev.map(c => c.id === editingCategory.id ? res.data : c));
        toast.success('Category updated');
      } else {
        const res = await adminAPI.createCategory(payload);
        setCategories(prev => [...prev, res.data]);
        toast.success('Category created');
      }
      closeCategoryForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (catId, catName) => {
    if (!window.confirm(`Delete category "${catName}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteCategory(catId);
      setCategories(prev => prev.filter(c => c.id !== catId));
      toast.success('Category deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete category'));
    }
  };

  const handleToggleCategoryActive = async (cat) => {
    try {
      const res = await adminAPI.updateCategory(cat.id, { active: !cat.active });
      setCategories(prev => prev.map(c => c.id === cat.id ? res.data : c));
      toast.success(cat.active ? 'Category deactivated' : 'Category activated');
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  // Make (marca) handlers
  const openMakeForm = (mk = null) => {
    if (mk) {
      setEditingMake(mk);
      setMakeForm({
        name: mk.name,
        categories: mk.categories || [],
        status: mk.status || 'active',
        display_order: mk.display_order ?? 0,
      });
    } else {
      setEditingMake(null);
      setMakeForm({ name: '', categories: [], status: 'active', display_order: makes.length });
    }
    setShowMakeForm(true);
  };

  const closeMakeForm = () => {
    setShowMakeForm(false);
    setEditingMake(null);
  };

  const handleSaveMake = async () => {
    if (!makeForm.name.trim()) {
      toast.error('El nombre de la marca es obligatorio');
      return;
    }
    setMakeSaving(true);
    try {
      const payload = {
        name: makeForm.name.trim(),
        categories: makeForm.categories,
        status: makeForm.status,
        display_order: Number(makeForm.display_order) || 0,
      };
      if (editingMake) {
        const res = await adminAPI.updateMake(editingMake.id, payload);
        setMakes(prev => prev.map(m => m.id === editingMake.id ? res.data : m));
        toast.success('Marca actualizada');
      } else {
        const res = await adminAPI.createMake(payload);
        setMakes(prev => [...prev, res.data]);
        toast.success('Marca creada');
      }
      closeMakeForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la marca'));
    } finally {
      setMakeSaving(false);
    }
  };

  const handleDeleteMake = async (mkId, mkName) => {
    if (!window.confirm(`¿Eliminar la marca "${mkName}"? Esta acción no se puede deshacer.`)) return;
    try {
      await adminAPI.deleteMake(mkId);
      setMakes(prev => prev.filter(m => m.id !== mkId));
      toast.success('Marca eliminada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar la marca'));
    }
  };

  const handleToggleMakeStatus = async (mk, newStatus) => {
    try {
      const res = await adminAPI.updateMake(mk.id, { status: newStatus });
      setMakes(prev => prev.map(m => m.id === mk.id ? res.data : m));
      toast.success(newStatus === 'active' ? 'Marca activada' : (newStatus === 'inactive' ? 'Marca desactivada' : 'Marca marcada como pendiente'));
    } catch (error) {
      toast.error('No se pudo actualizar la marca');
    }
  };

  const toggleMakeCategoryInForm = (catId) => {
    setMakeForm(f => ({
      ...f,
      categories: f.categories.includes(catId)
        ? f.categories.filter(c => c !== catId)
        : [...f.categories, catId],
    }));
  };

  const filteredMakes = makes.filter(m => makeStatusFilter === 'all' ? true : m.status === makeStatusFilter);
  const pendingMakesCount = makes.filter(m => m.status === 'pending').length;

  // Filter data
  const filteredUsers = users.filter(u => {
    if (userFilter && u.role !== userFilter) return false;
    if (userSearch && !u.name.toLowerCase().includes(userSearch.toLowerCase()) && 
        !u.email.toLowerCase().includes(userSearch.toLowerCase())) return false;
    return true;
  });

  const filteredListings = listings.filter(l => {
    if (listingFilter && l.status !== listingFilter) return false;
    if (listingSearch && !`${l.make} ${l.model}`.toLowerCase().includes(listingSearch.toLowerCase())) return false;
    return true;
  });

  // Prepare chart data
  const categoryData = analytics ? Object.entries(analytics.listings_by_category).map(([name, value]) => ({
    name: name.length > 10 ? name.slice(0, 10) + '...' : name,
    value,
  })) : [];

  const statusData = analytics?.listings_by_status ? Object.entries(analytics.listings_by_status).map(([name, value]) => ({
    name,
    value,
  })) : [];

  const roleData = analytics ? Object.entries(analytics.users_by_role).map(([name, value]) => ({
    name,
    value,
  })) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary p-8">
        <div className="max-w-[1400px] mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage users, listings, and platform settings
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Total de usuarios</p>
                <p className="text-3xl font-bold font-heading">{analytics?.total_users || 0}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-sm">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-listings">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Total de anuncios</p>
                <p className="text-3xl font-bold font-heading">{analytics?.total_listings || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-sm">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-active">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Anuncios activos</p>
                <p className="text-3xl font-bold font-heading">{analytics?.active_listings || 0}</p>
              </div>
              <div className="bg-accent/10 p-3 rounded-sm">
                <TrendingUp className="h-6 w-6 text-accent" />
              </div>
            </div>
          </div>
          <div className="stat-card" data-testid="stat-messages">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Mensajes</p>
                <p className="text-3xl font-bold font-heading">{analytics?.total_messages || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-sm">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="uppercase">Resumen</TabsTrigger>
            <TabsTrigger value="users" className="uppercase">Usuarios</TabsTrigger>
            <TabsTrigger value="listings" className="uppercase">Anuncios</TabsTrigger>
            <TabsTrigger value="categories" className="uppercase" data-testid="admin-categories-tab">Categorías</TabsTrigger>
            <TabsTrigger value="makes" className="uppercase" data-testid="admin-makes-tab">
              Marcas
              {pendingMakesCount > 0 && (
                <Badge className="ml-2 bg-yellow-100 text-yellow-800">{pendingMakesCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="billing" className="uppercase" data-testid="admin-billing-tab">Facturación</TabsTrigger>
            <TabsTrigger value="stripe" className="uppercase" data-testid="admin-stripe-tab">Stripe</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Listings by Category */}
              <div className="bg-white border border-border p-6 rounded-md">
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">
                  Listings by Category
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#FF4F00" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Listings by Status */}
              <div className="bg-white border border-border p-6 rounded-md">
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">
                  Listings by Status
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Users by Role */}
              <div className="bg-white border border-border p-6 rounded-md md:col-span-2">
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">
                  Users by Role
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={roleData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#0F172A" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white border border-border p-6 rounded-md">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                    data-testid="user-search"
                  />
                </div>
                <Select value={userFilter || 'all'} onValueChange={(value) => setUserFilter(value === 'all' ? '' : value)}>
                  <SelectTrigger className="w-[180px]" data-testid="user-filter">
                    <SelectValue placeholder="Todos los roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los roles</SelectItem>
                    <SelectItem value="buyer">Compradores</SelectItem>
                    <SelectItem value="seller">Vendedores</SelectItem>
                    <SelectItem value="dealer">Concesionarios</SelectItem>
                    <SelectItem value="admin">Administradores</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Usuario</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Rol</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Se unió</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Estado</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold uppercase tracking-wide">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-secondary/50" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-semibold">{u.name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="capitalize">{u.role}</Badge>
                        </td>
                        <td className="py-3 px-4 capitalize">{u.subscription_plan}</td>
                        <td className="py-3 px-4 text-sm">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-4">
                          {u.status === 'suspended' ? (
                            <Badge className="bg-red-500 text-white">Suspendido</Badge>
                          ) : (
                            <Badge className="bg-green-500 text-white">Activo</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {u.role !== 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSuspendUser(u.id, u.status !== 'suspended')}
                              className={u.status === 'suspended' ? 'text-green-600' : 'text-red-600'}
                              data-testid={`toggle-suspend-${u.id}`}
                            >
                              {u.status === 'suspended' ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />Activar</>
                              ) : (
                                <>
                                  <Ban className="h-4 w-4 mr-1" />Suspender</>
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <div className="bg-white border border-border p-6 rounded-md">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar anuncios..."
                    value={listingSearch}
                    onChange={(e) => setListingSearch(e.target.value)}
                    className="pl-10"
                    data-testid="listing-search"
                  />
                </div>
                <Select value={listingFilter || 'all'} onValueChange={(value) => setListingFilter(value === 'all' ? '' : value)}>
                  <SelectTrigger className="w-[180px]" data-testid="listing-filter">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="sold">Vendido</SelectItem>
                    <SelectItem value="archived">Archivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Listings Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Anuncio</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Precio</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Vendedor</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold uppercase tracking-wide">Destacado</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold uppercase tracking-wide">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredListings.map((l) => (
                      <tr key={l.id} className="border-b border-border hover:bg-secondary/50" data-testid={`listing-row-${l.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={l.images?.[0]?.image_url || 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=60&h=60&fit=crop'}
                              alt={l.make}
                              className="w-12 h-10 object-cover rounded-sm"
                            />
                            <div>
                              <p className="font-semibold">{l.year} {l.make} {l.model}</p>
                              <p className="text-sm text-muted-foreground">{l.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-semibold">{formatPrice(l.price)}</td>
                        <td className="py-3 px-4 text-sm">{l.seller_name}</td>
                        <td className="py-3 px-4">
                          <Badge className={`${getStatusBadgeColor(l.status)} text-white`}>
                            {l.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {l.featured ? (
                            <Star className="h-5 w-5 text-accent fill-accent" />
                          ) : (
                            <StarOff className="h-5 w-5 text-muted-foreground" />
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/listings/${l.id}`)}
                              data-testid={`view-listing-${l.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/listings/${l.id}/edit`)}
                              className="text-blue-600"
                              data-testid={`edit-listing-${l.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleFeatureListing(l.id, !l.featured)}
                              className={l.featured ? 'text-accent' : ''}
                              data-testid={`feature-listing-${l.id}`}
                            >
                              {l.featured ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}
                            </Button>
                            {l.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApproveListing(l.id)}
                                className="text-green-600"
                                data-testid={`approve-listing-${l.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />Aprobar</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" data-testid="admin-categories-content">
            <div className="bg-white border border-border rounded-md">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight">
                  Categories ({categories.length})
                </h2>
                <Button className="btn-accent" size="sm" onClick={() => openCategoryForm()} data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="categories-table">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Orden</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Nombre</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Slug</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Subcategorías</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Anuncios</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Estado</th>
                      <th className="text-right py-3 px-4 font-semibold uppercase text-xs tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map(cat => (
                      <tr key={cat.id} className={`border-b border-border/50 hover:bg-secondary/30 ${!cat.active ? 'opacity-50' : ''}`}>
                        <td className="py-3 px-4 text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <GripVertical className="h-3 w-3 text-muted-foreground/50" />
                            {cat.display_order}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-semibold">{cat.name}</span>
                          {cat.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{cat.description}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-secondary px-1.5 py-0.5 rounded">{cat.id}</code>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(cat.subcategories || []).slice(0, 3).map(sub => (
                              <Badge key={sub} variant="outline" className="text-xs">{sub}</Badge>
                            ))}
                            {(cat.subcategories || []).length > 3 && (
                              <Badge variant="outline" className="text-xs">+{cat.subcategories.length - 3}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono">{cat.listing_count ?? '-'}</td>
                        <td className="py-3 px-4">
                          <Badge className={cat.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                            {cat.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCategoryForm(cat)}
                              data-testid={`edit-category-${cat.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleCategoryActive(cat)}
                              className={cat.active ? 'text-yellow-600' : 'text-green-600'}
                              data-testid={`toggle-category-${cat.id}`}
                            >
                              {cat.active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCategory(cat.id, cat.name)}
                              className="text-red-600"
                              data-testid={`delete-category-${cat.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Category Form Modal */}
            {showCategoryForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4" data-testid="category-form-modal">
                <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight">
                      {editingCategory ? 'Edit Category' : 'New Category'}
                    </h3>
                    <Button variant="ghost" size="icon" onClick={closeCategoryForm} data-testid="close-category-form">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Name *</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={e => setCategoryForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g., Heavy Machinery"
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Descripción</Label>
                      <Textarea
                        value={categoryForm.description}
                        onChange={e => setCategoryForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Breve descripción de esta categoría"
                        rows={2}
                        data-testid="category-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">URL de imagen</Label>
                      <Input
                        value={categoryForm.image_url}
                        onChange={e => setCategoryForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="https://..."
                        data-testid="category-image-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Subcategorías</Label>
                      <Input
                        value={categoryForm.subcategories}
                        onChange={e => setCategoryForm(f => ({ ...f, subcategories: e.target.value }))}
                        placeholder="Clase 1-3, Clase 4-6, Tractocamiones (separadas por coma)"
                        data-testid="category-subcategories-input"
                      />
                      <p className="text-xs text-muted-foreground">Separa varias subcategorías con comas</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold uppercase tracking-wide">Orden de visualización</Label>
                        <Input
                          type="number"
                          value={categoryForm.display_order}
                          onChange={e => setCategoryForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
                          data-testid="category-order-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold uppercase tracking-wide">Activo</Label>
                        <div className="flex items-center gap-2 pt-2">
                          <Switch
                            checked={categoryForm.active}
                            onCheckedChange={v => setCategoryForm(f => ({ ...f, active: v }))}
                            data-testid="category-active-switch"
                          />
                          <span className="text-sm text-muted-foreground">{categoryForm.active ? 'Active' : 'Inactive'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <Button variant="outline" onClick={closeCategoryForm}>Cancelar</Button>
                    <Button className="btn-accent" onClick={handleSaveCategory} disabled={categorySaving} data-testid="save-category-btn">
                      {categorySaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Makes Tab */}
          <TabsContent value="makes" data-testid="admin-makes-content">
            <div className="bg-white border border-border rounded-md">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <h2 className="font-heading text-lg font-bold uppercase tracking-tight">
                    Marcas ({filteredMakes.length})
                  </h2>
                  <select
                    value={makeStatusFilter}
                    onChange={e => setMakeStatusFilter(e.target.value)}
                    className="text-sm border border-border rounded-sm px-2 py-1"
                    data-testid="make-status-filter"
                  >
                    <option value="all">Todas</option>
                    <option value="active">Activas</option>
                    <option value="pending">Pendientes</option>
                    <option value="inactive">Inactivas</option>
                  </select>
                </div>
                <Button className="btn-accent" size="sm" onClick={() => openMakeForm()} data-testid="add-make-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva marca
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="makes-table">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Nombre</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Slug</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Categorías</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Anuncios</th>
                      <th className="text-left py-3 px-4 font-semibold uppercase text-xs tracking-wider">Estado</th>
                      <th className="text-right py-3 px-4 font-semibold uppercase text-xs tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMakes.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 px-4 text-center text-muted-foreground">
                          No hay marcas para mostrar.
                        </td>
                      </tr>
                    )}
                    {filteredMakes.map(mk => (
                      <tr key={mk.id} className={`border-b border-border/50 hover:bg-secondary/30 ${mk.status === 'inactive' ? 'opacity-50' : ''}`}>
                        <td className="py-3 px-4">
                          <span className="font-semibold">{mk.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-secondary px-1.5 py-0.5 rounded">{mk.slug}</code>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-[260px]">
                            {(mk.categories || []).length === 0 ? (
                              <Badge variant="outline" className="text-xs">Todas</Badge>
                            ) : (
                              (mk.categories || []).map(catId => {
                                const cat = categories.find(c => c.id === catId);
                                return <Badge key={catId} variant="outline" className="text-xs">{cat ? cat.name : catId}</Badge>;
                              })
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono">{mk.listing_count ?? '-'}</td>
                        <td className="py-3 px-4">
                          {mk.status === 'active' && <Badge className="bg-green-100 text-green-700">Activa</Badge>}
                          {mk.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>}
                          {mk.status === 'inactive' && <Badge className="bg-gray-100 text-gray-500">Inactiva</Badge>}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            {mk.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleMakeStatus(mk, 'active')}
                                className="text-green-600"
                                data-testid={`approve-make-${mk.id}`}
                                title="Aprobar"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMakeForm(mk)}
                              data-testid={`edit-make-${mk.id}`}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {mk.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleMakeStatus(mk, 'inactive')}
                                className="text-yellow-600"
                                data-testid={`deactivate-make-${mk.id}`}
                                title="Desactivar"
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            ) : mk.status === 'inactive' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleMakeStatus(mk, 'active')}
                                className="text-green-600"
                                data-testid={`activate-make-${mk.id}`}
                                title="Activar"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMake(mk.id, mk.name)}
                              className="text-red-600"
                              data-testid={`delete-make-${mk.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Make Form Modal */}
            {showMakeForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4" data-testid="make-form-modal">
                <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight">
                      {editingMake ? 'Editar marca' : 'Nueva marca'}
                    </h3>
                    <Button variant="ghost" size="icon" onClick={closeMakeForm} data-testid="close-make-form">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Nombre *</Label>
                      <Input
                        value={makeForm.name}
                        onChange={e => setMakeForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Ej. Freightliner, Toyota, Caterpillar"
                        data-testid="make-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Categorías donde aparece</Label>
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-border rounded-sm" data-testid="make-categories-picker">
                        {categories.map(cat => {
                          const selected = makeForm.categories.includes(cat.id);
                          return (
                            <button
                              type="button"
                              key={cat.id}
                              onClick={() => toggleMakeCategoryInForm(cat.id)}
                              className={`px-3 py-1 rounded-sm text-xs border transition-colors ${selected ? 'bg-accent text-white border-accent' : 'bg-white text-foreground border-border hover:bg-secondary'}`}
                              data-testid={`make-cat-toggle-${cat.id}`}
                            >
                              {cat.name}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">Deja vacío para que aparezca en todas las categorías.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold uppercase tracking-wide">Orden</Label>
                        <Input
                          type="number"
                          value={makeForm.display_order}
                          onChange={e => setMakeForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
                          data-testid="make-order-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold uppercase tracking-wide">Estado</Label>
                        <select
                          value={makeForm.status}
                          onChange={e => setMakeForm(f => ({ ...f, status: e.target.value }))}
                          className="w-full border border-border rounded-sm px-3 py-2 text-sm"
                          data-testid="make-status-select"
                        >
                          <option value="active">Activa</option>
                          <option value="pending">Pendiente</option>
                          <option value="inactive">Inactiva</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <Button variant="outline" onClick={closeMakeForm}>Cancelar</Button>
                    <Button className="btn-accent" onClick={handleSaveMake} disabled={makeSaving} data-testid="save-make-btn">
                      {makeSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      {editingMake ? 'Guardar cambios' : 'Crear marca'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" data-testid="admin-billing-content">
            <BillingTab />
          </TabsContent>

          {/* Stripe Tab */}
          <TabsContent value="stripe" data-testid="admin-stripe-content">
            <StripeTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
